/**
 * Document download utility.
 *
 * - Fetches the document blob from the Jerry download endpoint with an
 *   Authorization header.
 * - Streams the response body via ReadableStream (reporting incremental
 *   progress) when the size is large OR unknown; otherwise reads a single blob.
 * - Parses the filename from the Content-Disposition header (supports
 *   `filename*=UTF-8''...`, quoted `filename="..."` (including embedded `;`),
 *   and bare `filename=...` forms) and triggers a browser download via a
 *   temporary <a download> element.
 * - Non-2xx responses are surfaced as `DownloadError` carrying the HTTP status
 *   so callers can map 403/404 to user messages without string matching.
 * - Accepts an optional AbortSignal so callers can cancel an in-flight download
 *   (page unmount, superseding download); the reader lock is always released.
 */

/** Threshold above which downloads stream and report progress. */
const LARGE_FILE_THRESHOLD = 5 * 1024 * 1024;

/** Progress callback: (bytesReceived, totalBytesOrNull). */
export type DownloadProgressHandler = (
  received: number,
  total: number | null,
) => void;

/**
 * Error carrying the HTTP status (and, when the server's error envelope
 * includes one, its business `code`) of a failed download so callers can
 * classify 403/404/etc. — and, within one status, distinguish business
 * reasons like `DRAFT_DOC_NO_MILESTONE` vs `DRAFT_DOC_BODY_EMPTY` — without
 * matching the message string.
 */
export class DownloadError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'DownloadError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Extract a UTF-8 decoded filename from a Content-Disposition header value.
 * Returns null when no filename is declared.
 */
function parseFilenameFromContentDisposition(header: string): string | null {
  if (!header) return null;
  // Prefer RFC 5987 `filename*=UTF-8''<percent-encoded>` form first.
  const starMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (starMatch?.[1]) {
    try {
      return decodeURIComponent(starMatch[1].trim());
    } catch {
      return starMatch[1].trim();
    }
  }
  // Quoted form: capture everything up to the closing quote so a `;` inside the
  // filename does not truncate it.
  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1].trim();
  // Bare form: stops at the next `;` (no quotes to protect a literal `;`).
  const plainMatch = header.match(/filename=([^;]+)/i);
  return plainMatch?.[1] ? plainMatch[1].trim() : null;
}

/**
 * Trigger a browser download for the given Jerry document.
 *
 * @param documentId        Jerry document id.
 * @param fallbackFilename  Filename used when the server omits Content-Disposition.
 * @param token             Bearer token; empty string skips the Authorization header.
 * @param onProgress        Optional progress callback for streamed files.
 * @param signal            Optional AbortSignal to cancel the in-flight download.
 */
export async function downloadDocument(
  documentId: string,
  fallbackFilename: string,
  token: string,
  onProgress?: DownloadProgressHandler,
  signal?: AbortSignal,
): Promise<void> {
  return downloadFromUrl(
    `/api/v1/jerry/documents/${documentId}/download`,
    fallbackFilename,
    token,
    onProgress,
    signal,
  );
}

/**
 * Trigger a browser download of a workbench draft's body rendered as docx
 * (顶栏「导出」，服务端 GET /draft-docs/{id}/export)。
 */
export async function downloadDraftDocExport(
  draftDocId: string,
  fallbackFilename: string,
  token: string,
  onProgress?: DownloadProgressHandler,
  signal?: AbortSignal,
): Promise<void> {
  return downloadFromUrl(
    `/api/v1/jerry/draft-docs/${draftDocId}/export`,
    fallbackFilename,
    token,
    onProgress,
    signal,
  );
}

/**
 * Trigger a browser download of a workbench draft's 留痕版（当前正文 vs 上一
 * 正文里程碑，Word 原生 w:ins/w:del 修订标记，服务端
 * GET /draft-docs/{id}/export-redline）。批次三②，人工修订留痕评估见
 * docs/research/2026-08-26-人工修订留痕评估.md。
 */
export async function downloadDraftDocExportRedline(
  draftDocId: string,
  fallbackFilename: string,
  token: string,
  onProgress?: DownloadProgressHandler,
  signal?: AbortSignal,
): Promise<void> {
  return downloadFromUrl(
    `/api/v1/jerry/draft-docs/${draftDocId}/export-redline`,
    fallbackFilename,
    token,
    onProgress,
    signal,
  );
}

/** 1B-3：导出合同要素提取任务的 Excel（GET /extraction-jobs/{id}/export?format=xlsx）。 */
export async function downloadExtractionJobExport(
  jobId: string,
  fallbackFilename: string,
  token: string,
  onProgress?: DownloadProgressHandler,
  signal?: AbortSignal,
): Promise<void> {
  return downloadFromUrl(
    `/api/v1/jerry/extraction-jobs/${jobId}/export?format=xlsx`,
    fallbackFilename,
    token,
    onProgress,
    signal,
  );
}

/**
 * 审查意见导出格式：与后端 `ai-review/export` 的 `format` 参数一致。
 *
 * A3 三种——markdown 纯文本、docx Word 意见书、xlsx 审查发现表——均脱离原文
 * 重新排版；B3 新增 `docx-annotated`（带批注的 Word）与前三者不同：正文保持
 * 原文不动，逐条发现以 Word 批注锚定在对应段落，供律师在 Word/WPS 里边看
 * 批注边改稿。
 */
export type AiReviewExportFormat = "markdown" | "docx" | "xlsx" | "docx-annotated";

/**
 * 下载某文档最近一次 AI 审查的意见书（GET /documents/{id}/ai-review/export）。
 *
 * AF2：审查工作台此前没有任何方式把结论变成一份能发给客户或合伙人的文件；
 * 起草与尽调都有导出，唯独审查没有。与其它导出同一条下载管线，不另造机制。
 *
 * A3：三种格式——markdown 纯文本、docx Word 意见书（律师交付客户的习惯载体）、
 * xlsx 审查发现表（供批量勾稽）。缺省 markdown，与历史行为一致。
 *
 * B3：`docx-annotated` 仅支持源文档是 Word 原文（docx/doc）。源文档为 PDF
 * 等其他格式 → 409；.doc 源文件转 docx 失败/超时/转换服务未启用 →
 * 502/504/415（415 对齐 `/preview/docx` 同一转换器的既有口径）。四种状态码
 * 文案均由服务端给出（见 useAiReviewExportDownload 的错误映射：400/409/415/
 * 502/504 均未预置固定文案，透传服务端 message，避免用统一措辞掩盖"不支持
 * 该源格式"与"转换失败"这几种下一步动作完全不同的原因）。
 */
export async function downloadAiReviewExport(
  documentId: string,
  fallbackFilename: string,
  token: string,
  onProgress?: DownloadProgressHandler,
  signal?: AbortSignal,
  format: AiReviewExportFormat = "markdown",
): Promise<void> {
  return downloadFromUrl(
    `/api/v1/jerry/documents/${documentId}/ai-review/export?format=${format}`,
    fallbackFilename,
    token,
    onProgress,
    signal,
  );
}

/**
 * Trigger a browser download of the redacted copy of a Jerry document
 * (一键脱敏下载，GET /documents/{id}/download-redacted)。
 *
 * Server returns 409 when redact_status !== ready; otherwise streams the
 * redacted file. Same blob/progress/abort contract as {@link downloadDocument}.
 */
export async function downloadRedactedDocument(
  documentId: string,
  fallbackFilename: string,
  token: string,
  onProgress?: DownloadProgressHandler,
  signal?: AbortSignal,
): Promise<void> {
  return downloadFromUrl(
    `/api/v1/jerry/documents/${documentId}/download-redacted`,
    fallbackFilename,
    token,
    onProgress,
    signal,
  );
}

/**
 * Shared download pipeline: authorized fetch → Content-Disposition filename →
 * blob (streamed with progress for large/unknown sizes) → anchor click.
 */
async function downloadFromUrl(
  url: string,
  fallbackFilename: string,
  token: string,
  onProgress?: DownloadProgressHandler,
  signal?: AbortSignal,
): Promise<void> {
  const resp = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    signal,
  });

  if (!resp.ok) {
    const errData = await resp
      .json()
      .catch(() => ({ error: { message: `下载失败 (${resp.status})` } }));
    const msg =
      (errData?.error?.message as string | undefined) ??
      `下载失败 (${resp.status})`;
    const code = errData?.error?.code as string | undefined;
    throw new DownloadError(msg, resp.status, code);
  }

  const contentDisposition = resp.headers.get('content-disposition') ?? '';
  const filename =
    parseFilenameFromContentDisposition(contentDisposition) ??
    fallbackFilename;

  const contentLength = Number(resp.headers.get('content-length') ?? 0);
  // Stream when the body is large OR its size is unknown (no/zero
  // content-length, e.g. chunked transfer) so progress still works for large
  // files served without a length header; small known files take the cheap path.
  const useStream =
    !!resp.body &&
    (contentLength === 0 || contentLength > LARGE_FILE_THRESHOLD);

  let blob: Blob;
  if (useStream && resp.body) {
    const reader = resp.body.getReader();
    const chunks: BlobPart[] = [];
    let received = 0;
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) {
          // Push the Uint8Array view itself, not its underlying ArrayBuffer:
          // `.buffer` may be larger than the chunk (pooled), corrupting bytes.
          chunks.push(value);
          received += value.length;
          onProgress?.(received, contentLength || null);
        }
      }
    } finally {
      // Release the lock so an aborted/failed stream doesn't leak the reader.
      reader.releaseLock();
    }
    blob = new Blob(chunks);
  } else {
    blob = await resp.blob();
  }

  triggerBrowserDownload(blob, filename);
}

/**
 * Create an object URL, click a hidden anchor, then revoke.
 *
 * Exported for the report-export module (PDF/Word/Markdown build-from-payload
 * pipelines), which assemble their own blobs from in-memory payloads.
 */
export function triggerBrowserDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/**
 * Trigger a browser download for text generated client-side (AI 回复导出、
 * 报告另存等)，无需服务端往返。
 *
 * 与 {@link downloadDocument} 系列的区别：那些拉取服务端已有的文件，本函数把
 * 内存里的文本直接交给浏览器。扩展名由调用方通过 `filename` 决定；缺失时按
 * `defaultExtension` 补齐，避免下载出没有后缀、双击打不开的文件。
 */
export function downloadTextAsFile(
  text: string,
  filename: string,
  mimeType = 'text/markdown; charset=utf-8',
  defaultExtension = '.md',
): void {
  const name = filename.toLowerCase().endsWith(defaultExtension)
    ? filename
    : `${filename}${defaultExtension}`;
  triggerBrowserDownload(new Blob([text], { type: mimeType }), name);
}
